import { useEffect, useReducer, useCallback, useRef } from "react";
import { ReactDataContext } from "@themost/react";
import { reducer } from '../reducers/evaluationFiltersReducer'
import {
    SET_PAGE_ACTION,
    SET_TOTAL_ACTION,
    UPDATE_MULTIPLE_VALUES_ACTION,
    UPDATE_STUDIES_ACTION
} from "../reducers/evaluationQueryReducer";


export const defaultState = {
    evaluation: [],
    page: 0,
    total: 0,
    loading: true,
    departments: [],
    countries: [],
    departmentalCoordinators: []
}

export const useEvaluationQuery = (filters, userService, langCode) => {

    const [state, dispatch] = useReducer(reducer, defaultState)

    const context = new ReactDataContext(process.env.REACT_APP_API_SERVER);
    context.setBearerAuthorization(userService.getToken());

    const mountedRestOptions = useRef(false);

    const setPage = useCallback((page) => {
        dispatch({ type: SET_PAGE_ACTION, payload: { page } })
    }, [])

    const updateMultipleValues = useCallback((data) => {
        dispatch({ type: UPDATE_MULTIPLE_VALUES_ACTION, payload: data })
    }, []);

    const updateEvaluation = useCallback((data) => {
        dispatch({ type: UPDATE_STUDIES_ACTION, payload: data })
    }, [])

    const setTotal = useCallback((data) => {
        dispatch({ type: SET_TOTAL_ACTION, payload: data })
    }, [])

    const hasMandatoryFilters = useCallback(() => {
        return Boolean(filters.state.departmentSelected && filters.state.dateSelected);
    }, [filters.state.departmentSelected, filters.state.dateSelected]);

    const fetchEvaluation = async (pageNumber) => {
        if (!hasMandatoryFilters()) {
            updateMultipleValues({
                evaluation: [],
                loading: false,
                total: 0,
                page: 0
            });
            return;
        }

        let fields = [];
        if (filters.state.idSelected) {
            fields.push({
                "key": "applicationNumber",
                "value": filters.state.idSelected
            })
        }

        let query = context.model(`ApplicationStudentStudiesForms`).asQueryable().where("id").greaterThan(0)
            .and("isDeleted").equal(false).and("status").equal("submitted");

        if (filters.state.dateSelected) {
            query = query.and('applicationYear').equal(filters.state.dateSelected)
        }

        if (filters.state.departmentSelected) {
            const departmentQueryField = langCode === "gr" ? "studiesData/department/nameGR" : "studiesData/department/name";
            query = query.and(departmentQueryField).equal(filters.state.departmentSelected)
        }

        if (filters.state.universitySelected) {
            query = query.and("studiesData/department/institution/name").equal(filters.state.universitySelected)
        }

        if (filters.state.erasmusCodeSelected) {
            query = query.and("studiesData/department/institution/erasmusCode").equal(filters.state.erasmusCodeSelected)
        }

        if (filters.state.countrySelected) {
            query = query.and("studiesData/department/institution/country").equal(filters.state.countrySelected)
        }

        if (filters.state.departmentalCoordinatorSelected) {
            query = query.and("prefferedMobility/iiaId/departmentalCoordinatorContacts/id").equal(filters.state.departmentalCoordinatorSelected)
        }
    
        try {
            const studentEvaluationResults = await query.getItems();
            const normalizedSelectedCoordinatorId = filters.state.departmentalCoordinatorSelected ? String(filters.state.departmentalCoordinatorSelected) : null;
            const filteredBySecondCoordinator = normalizedSelectedCoordinatorId
                ? studentEvaluationResults.filter((application) =>
                    (application?.prefferedMobility || []).some((mobility) =>
                        String(mobility?.iiaId?.departmentalCoordinatorContacts?.[1]?.id) === normalizedSelectedCoordinatorId
                    )
                )
                : studentEvaluationResults;

            updateMultipleValues({
                evaluation: filteredBySecondCoordinator,
                loading: false,
                total: filteredBySecondCoordinator.length,
                page: pageNumber
            })
        } catch (err) {
            console.log(err);
        }

    }

    //runs on first mount
    useEffect(async() => {
        let departments, countries, departmentalCoordinators;
        try {
            [departments, countries, departmentalCoordinators] = await Promise.all([
                context.model('MobilityDepartments').asQueryable().where('institution').equal(1).take(-1).getItems(),
                context.model('MobilityCountries').asQueryable().take(-1).getItems(),
                context.model('MobilityContacts').asQueryable().where('department/institution/id').equal(1).take(-1).getItems()
            ]);
        } catch (err) {
            console.log(err);
        }
        updateMultipleValues({
            departments: departments,
            countries: countries,
            loading: false,
            departmentalCoordinators: (departmentalCoordinators || []).map((contact) => ({
                ...contact,
                label: `${contact?.familyName || ""} ${contact?.givenName || ""}`.trim()
            }))
        })
    }, [])

    // runs when the user changes the rest of the options
    useEffect(() => {
        if (mountedRestOptions.current === false) {
            mountedRestOptions.current = true;
            return;
        }

        (async () => {
            if (!hasMandatoryFilters()) {
                updateMultipleValues({
                    evaluation: [],
                    loading: false,
                    total: 0,
                    page: 0
                });
                return;
            }
            fetchEvaluation(0);
        })()
        // fetchEvaluation(parentState.currentPage);
    }, [
        filters.state.idSelected,
        filters.state.departments,
        filters.state.typeSelected,
        filters.state.dateSelected,
        filters.state.departmentSelected,
        filters.state.universitySelected,
        filters.state.erasmusCodeSelected,
        filters.state.countrySelected,
        filters.state.departmentalCoordinatorSelected,
        hasMandatoryFilters
    ])

    return {
        evaluation: state.evaluation,
        loading: state.loading,
        page: state.page,
        total: state.total,
        departments: state.departments,
        countries: state.countries,
        departmentalCoordinators: state.departmentalCoordinators,
        setPage,
        updateEvaluation,
        setTotal
    }
}
